import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Assignment, ClassMember, RoutineAssignment } from '../types/alarm';
import { userDataService } from './userDataService';

// 과제 상태 타입
export type AssignmentStatusValue = 'pending' | 'confirmed' | 'delayed' | 'exempted' | 'excluded';

// 과제 정보 (모든 과제)
export interface AssignmentInfo {
  id: string;
  title: string;
  type: 'assignment' | 'project' | 'routine';
  subTaskId?: string; // 프로젝트 과제의 하위 과제 ID
  projectTitle?: string; // 프로젝트 과제의 상위 프로젝트 제목
  subTaskTitle?: string; // 프로젝트 과제의 하위 과제 제목
  dueDate?: Date; // 마감일
  hasIncompleteStudents: boolean; // 미완료 멤버가 있는지 여부
  categoryId?: string; // 카테고리 ID (일반 과제용)
  color?: string; // 프로젝트 색상 (프로젝트 과제용)
  routineAssignmentId?: string; // 루틴 과제 ID (루틴 과제용)
  dateId?: string; // 날짜 ID (루틴 과제용)
}

// 멤버별 과제 상태 정보
export interface StudentAssignmentStatus {
  studentId: string;
  studentName: string;
  studentNumber: string;
  assignmentStatuses: {
    [assignmentId: string]: {
      status: AssignmentStatusValue;
      assignmentTitle: string;
      assignmentType: 'assignment' | 'project';
      subTaskId?: string;
      isAssigned?: boolean; // 과제를 부여받았는지 여부
    };
  };
}

// 테이블 데이터 구조
export interface AssignmentTableData {
  members: ClassMember[];
  allAssignments: AssignmentInfo[];
  studentStatuses: StudentAssignmentStatus[];
  incompleteAssignments: Set<string>;
  incompleteSubTasks: Set<string>;
  categoriesData?: any[];
  projectsData?: any[];
}

class AssignmentTableServiceOptimized {
  
  /**
   * 최적화된 과제 데이터 로드
   */
  async loadAssignmentTableData(
    userId: string, 
    classId: string
  ): Promise<AssignmentTableData> {
    try {
      // 1. 클래스 멤버와 모든 과제를 병렬로 로드
      const [members, allAssignments] = await Promise.all([
        userDataService.loadClassMembers(userId, classId),
        this.loadAllAssignmentsOptimized(userId, classId)
      ]);

      // 2. 멤버별 과제 상태를 배치로 로드
      const studentStatuses = await this.loadStudentAssignmentStatusesBatch(
        userId, 
        classId, 
        members, 
        allAssignments
      );

      return {
        members,
        allAssignments,
        studentStatuses,
        incompleteAssignments: new Set<string>(),
        incompleteSubTasks: new Set<string>()
      };
    } catch (error) {
      console.error('과제 관리 테이블 데이터 로드 실패:', error);
      throw error;
    }
  }

  /**
   * 최적화된 모든 과제들 로드 (병렬 처리)
   */
  private async loadAllAssignmentsOptimized(
    userId: string, 
    classId: string
  ): Promise<AssignmentInfo[]> {
    try {
      // 일반 과제와 프로젝트 과제를 병렬로 로드
      const [regularAssignments, projectAssignments] = await Promise.all([
        this.loadRegularAssignmentsOptimized(userId, classId),
        this.loadProjectAssignmentsOptimized(userId, classId)
      ]);

      return [...regularAssignments, ...projectAssignments];
    } catch (error) {
      console.error('모든 과제 로드 실패:', error);
      return [];
    }
  }

  /**
   * 최적화된 일반 과제 로드
   */
  private async loadRegularAssignmentsOptimized(
    userId: string, 
    classId: string
  ): Promise<AssignmentInfo[]> {
    try {
      const assignmentsRef = collection(db, 'users', userId, 'classes', classId, 'generalAssignments');
      const assignmentsSnap = await getDocs(assignmentsRef);
      
      const assignments: AssignmentInfo[] = [];
      const assignmentIds: string[] = [];

      // 활성화된 과제들만 수집
      for (const assignmentDoc of assignmentsSnap.docs) {
        const assignment = assignmentDoc.data() as Assignment;
        
        if (assignment.isActive && assignment.assignedStudentIds?.length) {
          assignmentIds.push(assignment.id);
          
          let dueDate: Date | undefined;
          if (assignment.dueDate) {
            if (assignment.dueDate instanceof Timestamp) {
              dueDate = assignment.dueDate.toDate();
            } else if (assignment.dueDate instanceof Date) {
              dueDate = assignment.dueDate;
            } else if (typeof assignment.dueDate === 'string' || typeof assignment.dueDate === 'number') {
              dueDate = new Date(assignment.dueDate);
            }
          }

          assignments.push({
            id: assignment.id,
            title: assignment.title,
            type: 'assignment',
            dueDate: dueDate,
            hasIncompleteStudents: false, // 나중에 배치로 확인
            categoryId: assignment.categoryId // 카테고리 ID 추가
          });
        }
      }

      // 모든 과제의 미완료 상태를 한 번에 확인
      if (assignmentIds.length > 0) {
        const incompleteStatuses = await this.checkIncompleteStatusesBatch(
          userId, 
          classId, 
          'assignmentStatuses', 
          assignmentIds
        );

        // 미완료 상태 업데이트
        assignments.forEach(assignment => {
          assignment.hasIncompleteStudents = incompleteStatuses.has(assignment.id);
        });
      }

      return assignments;
    } catch (error) {
      console.error('일반 과제 로드 실패:', error);
      return [];
    }
  }

  /**
   * 최적화된 프로젝트 과제 로드
   */
  private async loadProjectAssignmentsOptimized(
    userId: string, 
    classId: string
  ): Promise<AssignmentInfo[]> {
    try {
      const projectAssignmentsRef = collection(db, 'users', userId, 'classes', classId, 'projectAssignments');
      const projectAssignmentsSnap = await getDocs(projectAssignmentsRef);
      
      const assignments: AssignmentInfo[] = [];
      const subTaskIds: { assignmentId: string; subTaskId: string }[] = [];

      for (const projectDoc of projectAssignmentsSnap.docs) {
        const projectAssignment = projectDoc.data() as any;
        
        if (!projectAssignment.isActive || !projectAssignment.assignedStudentIds?.length) {
          continue;
        }

        let dueDate: Date | undefined;
        if (projectAssignment.dueDate) {
          if (projectAssignment.dueDate instanceof Timestamp) {
            dueDate = projectAssignment.dueDate.toDate();
          } else if (projectAssignment.dueDate instanceof Date) {
            dueDate = projectAssignment.dueDate;
          } else if (typeof projectAssignment.dueDate === 'string' || typeof projectAssignment.dueDate === 'number') {
            dueDate = new Date(projectAssignment.dueDate);
          }
        }

        // 각 하위 과제에 대해 수집
        for (const subTask of projectAssignment.subTasks || []) {
          subTaskIds.push({ assignmentId: projectAssignment.id, subTaskId: subTask.id });
          
          assignments.push({
            id: projectAssignment.id,
            title: `${projectAssignment.title} - ${subTask.title}`, // 기존 호환성을 위해 유지
            type: 'project',
            subTaskId: subTask.id,
            projectTitle: projectAssignment.title,
            subTaskTitle: subTask.title,
            dueDate: dueDate,
            hasIncompleteStudents: false, // 나중에 배치로 확인
            color: projectAssignment.color // 프로젝트 색상 추가
          });
        }
      }

      // 모든 하위 과제의 미완료 상태를 한 번에 확인
      if (subTaskIds.length > 0) {
        const incompleteStatuses = await this.checkProjectIncompleteStatusesBatch(
          userId, 
          classId, 
          subTaskIds
        );

        // 미완료 상태 업데이트
        assignments.forEach(assignment => {
          const key = `${assignment.id}-${assignment.subTaskId}`;
          assignment.hasIncompleteStudents = incompleteStatuses.has(key);
        });
      }

      return assignments;
    } catch (error) {
      console.error('프로젝트 과제 로드 실패:', error);
      return [];
    }
  }

  /**
   * 배치로 미완료 상태 확인 (일반 과제)
   */
  private async checkIncompleteStatusesBatch(
    userId: string,
    classId: string,
    collectionName: string,
    assignmentIds: string[]
  ): Promise<Set<string>> {
    const incompleteAssignments = new Set<string>();

    try {
      // V3 구조에서 각 과제의 상태 문서를 병렬로 확인
      const statusPromises = assignmentIds.map(async (assignmentId) => {
        const statusRef = doc(
          db, 
          'users', 
          userId, 
          'classes', 
          classId, 
          collectionName, 
          assignmentId
        );
        const statusSnap = await getDoc(statusRef);
        
        if (!statusSnap.exists()) {
          return { assignmentId, hasPending: false };
        }
        
        const statusData = statusSnap.data();
        const studentStatuses = statusData.studentStatuses || [];
        
        const hasPending = studentStatuses.some((status: any) => 
          status.status === 'pending'
        );

        return { assignmentId, hasPending };
      });

      const results = await Promise.all(statusPromises);
      
      results.forEach(({ assignmentId, hasPending }) => {
        if (hasPending) {
          incompleteAssignments.add(assignmentId);
        }
      });

    } catch (error) {
      console.error('미완료 상태 확인 실패:', error);
    }

    return incompleteAssignments;
  }

  /**
   * 배치로 미완료 상태 확인 (프로젝트 과제)
   */
  private async checkProjectIncompleteStatusesBatch(
    userId: string,
    classId: string,
    subTaskIds: { assignmentId: string; subTaskId: string }[]
  ): Promise<Set<string>> {
    const incompleteSubTasks = new Set<string>();

    try {
      // V3 구조에서 각 하위 과제의 상태 문서를 병렬로 확인
      const statusPromises = subTaskIds.map(async ({ assignmentId, subTaskId }) => {
        const statusRef = doc(
          db,
          'users',
          userId,
          'classes',
          classId,
          'projectAssignmentStatuses',
          assignmentId,
          'subTasks',
          subTaskId
        );
        const statusSnap = await getDoc(statusRef);
        
        if (!statusSnap.exists()) {
          return { key: `${assignmentId}-${subTaskId}`, hasPending: false };
        }
        
        const statusData = statusSnap.data();
        const studentStatuses = statusData.studentStatuses || [];
        
        const hasPending = studentStatuses.some((status: any) => 
          status.status === 'pending'
        );

        return { key: `${assignmentId}-${subTaskId}`, hasPending };
      });

      const results = await Promise.all(statusPromises);
      
      results.forEach(({ key, hasPending }) => {
        if (hasPending) {
          incompleteSubTasks.add(key);
        }
      });

    } catch (error) {
      console.error('프로젝트 미완료 상태 확인 실패:', error);
    }

    return incompleteSubTasks;
  }

  /**
   * 배치로 멤버별 과제 상태 로드
   */
  private async loadStudentAssignmentStatusesBatch(
    userId: string,
    classId: string,
    members: ClassMember[],
    allAssignments: AssignmentInfo[]
  ): Promise<StudentAssignmentStatus[]> {
    // 멤버별로 병렬 처리
    const studentPromises = members.map(async (member) => {
      const assignmentStatuses: { [assignmentId: string]: any } = {};

      // 과제별로 병렬 처리
      const assignmentPromises = allAssignments.map(async (assignment) => {
        let status: AssignmentStatusValue = 'pending';
        let isAssigned = false;

        try {
          if (assignment.type === 'assignment') {
            // V3 구조: generalAssignmentStatuses/{assignmentId} 문서에서 studentStatuses 배열 조회
            const statusRef = doc(
              db,
              'users',
              userId,
              'classes',
              classId,
              'generalAssignmentStatuses',
              assignment.id
            );
            const statusSnap = await getDoc(statusRef);
            
            if (statusSnap.exists()) {
              const statusData = statusSnap.data();
              const studentStatuses = statusData.studentStatuses || [];
              const studentStatus = studentStatuses.find((s: any) => s.studentId === member.id);
              
              if (studentStatus) {
                status = studentStatus.status || 'pending';
                isAssigned = true;
              }
            }
          } else if (assignment.type === 'project' && assignment.subTaskId) {
            // V3 구조: projectAssignmentStatuses/{assignmentId}/subTasks/{subTaskId} 문서에서 studentStatuses 배열 조회
            const statusRef = doc(
              db,
              'users',
              userId,
              'classes',
              classId,
              'projectAssignmentStatuses',
              assignment.id,
              'subTasks',
              assignment.subTaskId
            );
            
            const statusSnap = await getDoc(statusRef);
            
            if (statusSnap.exists()) {
              const statusData = statusSnap.data();
              const studentStatuses = statusData.studentStatuses || [];
              const studentStatus = studentStatuses.find((s: any) => s.studentId === member.id);
              
              if (studentStatus) {
                status = studentStatus.status || 'pending';
                isAssigned = true;
              }
            }
          }

          // 프로젝트 과제의 경우 고유한 키를 위해 subTaskId를 포함
          const uniqueAssignmentId = assignment.type === 'project' && assignment.subTaskId 
            ? `${assignment.id}-${assignment.subTaskId}`
            : assignment.id;

          return {
            assignmentId: uniqueAssignmentId,
            status: {
              status,
              assignmentTitle: assignment.title,
              assignmentType: assignment.type,
              subTaskId: assignment.subTaskId,
              isAssigned
            }
          };
        } catch (error) {
          console.error(`멤버 ${member.id}의 과제 ${assignment.id} 상태 로드 실패:`, error);
          
          // 프로젝트 과제의 경우 고유한 키를 위해 subTaskId를 포함
          const uniqueAssignmentId = assignment.type === 'project' && assignment.subTaskId 
            ? `${assignment.id}-${assignment.subTaskId}`
            : assignment.id;
            
          return {
            assignmentId: uniqueAssignmentId,
            status: {
              status: 'pending' as AssignmentStatusValue,
              assignmentTitle: assignment.title,
              assignmentType: assignment.type,
              subTaskId: assignment.subTaskId,
              isAssigned: false
            }
          };
        }
      });

      const assignmentResults = await Promise.all(assignmentPromises);
      
      assignmentResults.forEach(({ assignmentId, status }) => {
        assignmentStatuses[assignmentId] = status;
      });

      return {
        studentId: member.id,
        studentName: member.name,
        studentNumber: member.number || '',
        assignmentStatuses
      };
    });

    const results = await Promise.all(studentPromises);
    return results;
  }

  /**
   * 루틴 과제 테이블 데이터 로드
   */
  async loadRoutineAssignmentTableData(
    userId: string, 
    classId: string,
    targetDate: Date = new Date()
  ): Promise<AssignmentTableData> {
    try {
      console.log('🔄 루틴 과제 테이블 데이터 로드 시작');

      // 1. 루틴 과제 목록 로드
      const routineAssignmentsRef = collection(
        db, 
        'users', 
        userId, 
        'classes', 
        classId, 
        'routineAssignments'
      );
      const routineAssignmentsSnap = await getDocs(routineAssignmentsRef);
      const routineAssignments: RoutineAssignment[] = routineAssignmentsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as RoutineAssignment));

      console.log('📋 로드된 루틴 과제 수:', routineAssignments.length);

      // 2. 선택된 날짜에 해당하는 루틴 과제 날짜들 생성
      const targetDateId = targetDate.getFullYear() + '-' + 
        String(targetDate.getMonth() + 1).padStart(2, '0') + '-' + 
        String(targetDate.getDate()).padStart(2, '0');
      

      const targetDateRoutineAssignments: AssignmentInfo[] = [];

      for (const routine of routineAssignments) {
        // 선택된 날짜가 이 루틴 과제의 반복 요일에 포함되는지 직접 확인
        const targetDayOfWeek = targetDate.getDay(); // 0-6 (일요일-토요일)
        const isTargetDateRoutineDay = routine.routineDays && routine.routineDays.includes(targetDayOfWeek);
        
        if (isTargetDateRoutineDay) {
          targetDateRoutineAssignments.push({
            id: `${routine.id}-${targetDateId}`, // 고유한 ID 생성
            title: routine.title,
            type: 'routine',
            dueDate: targetDate,
            hasIncompleteStudents: false, // 초기값, 나중에 멤버 상태 로드 시 업데이트
            routineAssignmentId: routine.id,
            dateId: targetDateId,
            color: routine.color || '#6366f1' // 루틴 과제 색상 (기본값: 인디고)
          });
        }
      }

      console.log('📅 선택된 날짜에 해당하는 루틴 과제 수:', targetDateRoutineAssignments.length);

      // 3. 멤버 목록 로드
      const membersRef = collection(db, 'users', userId, 'classes', classId, 'members');
      const membersSnap = await getDocs(membersRef);
      const members: ClassMember[] = membersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ClassMember));

      console.log('👥 로드된 멤버 수:', members.length);

      // 4. 멤버별 루틴 과제 상태 로드
      const studentStatuses = await this.loadRoutineStudentAssignmentStatuses(
        userId, 
        classId, 
        targetDateRoutineAssignments, 
        members
      );

      // 5. 미완료 멤버 여부 업데이트
      targetDateRoutineAssignments.forEach(assignment => {
        const hasIncomplete = studentStatuses.some(student => {
          const status = student.assignmentStatuses[assignment.id];
          return status && status.status === 'pending';
        });
        assignment.hasIncompleteStudents = hasIncomplete;
      });

      console.log('✅ 루틴 과제 테이블 데이터 로드 완료');

      return {
        members,
        allAssignments: targetDateRoutineAssignments,
        studentStatuses,
        incompleteAssignments: new Set<string>(),
        incompleteSubTasks: new Set<string>()
      };
    } catch (error) {
      console.error('❌ 루틴 과제 테이블 데이터 로드 실패:', error);
      throw error;
    }
  }

  /**
   * 루틴 과제 멤버 상태 로드
   */
  private async loadRoutineStudentAssignmentStatuses(
    userId: string,
    classId: string,
    assignments: AssignmentInfo[],
    members: ClassMember[]
  ): Promise<StudentAssignmentStatus[]> {
    // 먼저 루틴 과제의 assignedStudentIds를 로드
    const routineAssignmentsRef = collection(
      db, 
      'users', 
      userId, 
      'classes', 
      classId, 
      'routineAssignments'
    );
    const routineAssignmentsSnap = await getDocs(routineAssignmentsRef);
    const routineAssignments: RoutineAssignment[] = routineAssignmentsSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as RoutineAssignment));

    const studentPromises = members.map(async (member) => {
      const assignmentStatuses: { [assignmentId: string]: any } = {};

      // 각 루틴 과제에 대해 멤버 상태 로드
      for (const assignment of assignments) {
        if (!assignment.routineAssignmentId || !assignment.dateId) continue;

        // 해당 루틴 과제의 assignedStudentIds 확인
        const routineAssignment = routineAssignments.find(r => r.id === assignment.routineAssignmentId);
        const isStudentAssignedToRoutine = routineAssignment?.assignedStudentIds?.includes(member.id) || false;

        let status: AssignmentStatusValue = 'pending';
        let isAssigned = isStudentAssignedToRoutine; // 과제를 부여받았는지 여부

        // 과제를 부여받은 멤버만 상태 데이터 확인
        if (isStudentAssignedToRoutine) {
          try {
            // V3 구조: routineAssignmentStatuses/{routineId}/dates/{dateId} 문서에서 studentStatuses 배열 조회
            const statusRef = doc(
              db,
              'users',
              userId,
              'classes',
              classId,
              'routineAssignmentStatuses',
              assignment.routineAssignmentId,
              'dates',
              assignment.dateId
            );
            
            const statusSnap = await getDoc(statusRef);
            
            if (statusSnap.exists()) {
              const statusData = statusSnap.data();
              const studentStatuses = statusData.studentStatuses || [];
              const studentStatus = studentStatuses.find((s: any) => s.studentId === member.id);
              
              if (studentStatus) {
                status = studentStatus.status || 'pending';
              }
            }
            // 문서가 없어도 isAssigned는 true 유지 (과제를 부여받았지만 데이터가 없는 경우)
          } catch (error) {
            console.warn(`루틴 과제 상태 로드 실패 (${assignment.id}, ${member.id}):`, error);
          }
        }

        assignmentStatuses[assignment.id] = {
          status,
          assignmentTitle: assignment.title,
          assignmentType: 'routine' as const,
          isAssigned
        };
      }

      return {
        studentId: member.id,
        studentName: member.name,
        studentNumber: member.number || '',
        assignmentStatuses
      };
    });

    const results = await Promise.all(studentPromises);
    return results;
  }
}

export const assignmentTableServiceOptimized = new AssignmentTableServiceOptimized();
